import DocumentActionsSection from 'components/common/documents-upload/DocumentActionsSection';
import PageHeader from 'components/common/general/layouts/PageHeader';
import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { DocumentList } from 'components/common/documents-upload/DocumentsList';
import { useHistory } from 'react-router-dom';
import SubscriberIMQDocumentsPageStore, {
  SubscriberIMQDocumentsPageStoreContext,
  useSubscriberIMQDocumentsPageStore,
} from 'store/pages/subscriber/subscriber-imq-documents/pageStore';
import PageFooter from 'components/common/general/layouts/PageFooter';
import {
  getSubscriberTaskChecklistURL,
  SUBSCRIBER_CARD_DATA,
} from 'global/checklists-helpers/SubscriberChecklistHelper';
import useSubscriberTaskUrlParams from 'global/hooks/useSubscriberTaskUrlParams';
import GlobalLayout from 'components/common/general/layouts/GlobalLayout';
import SubscriberTaskSidebar from 'components/common/election/SubscriberTaskSidebar';
import { PageWrapperStyled } from '../../../globalStyles';

// TESTING_POLICY: CYPRESS_ONLY

const SubscriberIMQDocumentsPageSidebar: React.FC = observer(() => {
  const { group, checklistOptions } = useSubscriberIMQDocumentsPageStore();

  return <SubscriberTaskSidebar group={group} checklistOptions={checklistOptions} />;
});

const PageContent: React.FC = observer(() => {
  const pageStore = useSubscriberIMQDocumentsPageStore();
  const { groupId, subscriberId, entityId: cartId } = useSubscriberTaskUrlParams();

  if (!subscriberId) {
    throw new Error('Missing required subId query parameter');
  }

  const history = useHistory();

  useEffect(() => {
    void pageStore.loadInitialData();
  }, []);

  const onBack = (): void => {
    history.push(getSubscriberTaskChecklistURL({ entityId: cartId, subId: subscriberId, groupId }));
  };

  const onStepComplete = async (): Promise<void> => {
    await pageStore.completeStep();
    history.push(`/employee?groupId=${groupId}`);
  };

  return (
    <PageWrapperStyled>
      <PageHeader
        title={SUBSCRIBER_CARD_DATA.SUBSCRIBER_DOCUMENTS_IMQ.title}
        subtitle={SUBSCRIBER_CARD_DATA.SUBSCRIBER_DOCUMENTS_IMQ.text}
        subtitleMaxWidth="51rem"
        spacing="WITH_ACTION_BELOW"
      />
      <DocumentActionsSection
        requestDocumentModalStore={pageStore.requestDocumentModalStore}
        uploadDocumentModalStore={pageStore.uploadDocumentModalStore}
        loading={pageStore.isPageLoading}
        disableMutativeActions={pageStore.thisStepHasBeenCompleted}
      />
      <DocumentList
        activeDocuments={pageStore.activeDocuments}
        inactiveDocuments={pageStore.inactiveDocuments}
        loading={pageStore.isPageLoading}
      />
      <PageFooter
        disableCompleteButton={pageStore.thisStepHasBeenCompleted || !pageStore.pageDomainCompletion.allowed}
        completeDisabledReason={pageStore.pageDomainCompletion.notAllowedMessage}
        onComplete={onStepComplete}
        onBack={onBack}
      />
    </PageWrapperStyled>
  );
});

const SubscriberIMQDocumentsPage: React.FC = () => {
  const { subscriberId, entityId: cartId, groupId } = useSubscriberTaskUrlParams();

  if (!subscriberId) {
    throw new Error('Missing required subId query parameter');
  }

  const pageStore = useMemo(
    () =>
      new SubscriberIMQDocumentsPageStore({
        entityId: `${subscriberId}_${cartId}`,
        affiliatedId: cartId,
        groupId,
      }),
    [],
  );
  return (
    <SubscriberIMQDocumentsPageStoreContext.Provider value={pageStore}>
      <GlobalLayout leftSection={<SubscriberIMQDocumentsPageSidebar />} rightSection={<PageContent />} />
    </SubscriberIMQDocumentsPageStoreContext.Provider>
  );
};

export default SubscriberIMQDocumentsPage;
